import { Button, Modal } from '@morressier/alembic';
import { Flex, Spinner, Text } from '@chakra-ui/react';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import { useQuery } from '@tanstack/react-query';
import {
  EventItem,
  EventLabel,
  getTemplateByTemplateId
} from 'src/api/email-service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { getReminderEventsPhaseLabel } from './EmailTemplatesTable';

interface Props {
  onClose: () => void;
  editModal: (templateId: string) => void;
  isOpen: boolean;
  organizationId: string;
  templateId: string;
  events: EventItem[];
  labels: Record<string, EventLabel>;
}
enum Phases {
  sub = 'Original Submission',
  resub = 'Resubmission',
  rev = 'Revision'
}

export const triggerTypeLabelMapping = {
  AdhocEmailSent: 'Adhoc Event',
  Reminder: 'Reminder Event',
  SystemEvents: 'System Event'
} as const;

const getEventTriggerType = (event: string) => {
  if (event === 'AdhocEmailSent') {
    return triggerTypeLabelMapping[event];
  }

  if (event.toLowerCase().includes('reminder')) {
    return triggerTypeLabelMapping.Reminder;
  }

  return triggerTypeLabelMapping.SystemEvents;
};

const TemplatePreview = ({
  isOpen,
  onClose,
  organizationId,
  templateId,
  editModal,
  labels
}: Props) => {
  const { data: templateDetails } = useQuery({
    queryKey: ['email-template-details', templateId, organizationId],
    queryFn: () => getTemplateByTemplateId(templateId, organizationId),
    enabled: isOpen && !!templateId
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={{ base: 'calc(100vw - 16px)', md: '720px' } as any}
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex flexDirection="column" padding={{ base: 4, md: 6 }} gap={4}>
        <Flex justifyContent="flex-start">
          <FontAwesomeIcon
            icon={faEnvelope}
            style={{ height: '40px', width: '32px' }}
          />
        </Flex>

        {templateDetails ? (
          <Flex flexDirection="column" gap={3}>
            <Text textStyle="headingSection">{templateDetails.name}</Text>
            {templateDetails.description && (
              <Flex flexDirection="column">
                <Text textStyle="headingItem">Description:</Text>
                <Text>{templateDetails.description}</Text>
              </Flex>
            )}
            <Flex
              justifyContent="space-between"
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: 4, md: 0 }}
            >
              <Flex flexDirection="column">
                <Flex flexDirection="row" gap={2}>
                  <Text textStyle="headingItem">Status: </Text>
                  <Text>{templateDetails.active ? 'Active' : 'Inactive'}</Text>
                </Flex>
                <Flex flexDirection="row" gap={2}>
                  <Text textStyle="headingItem">Trigger Type:</Text>
                  <Text>{getEventTriggerType(templateDetails?.event)}</Text>
                </Flex>
                <Flex flexDirection="row" gap={2}>
                  <Text textStyle="headingItem">Event:</Text>
                  <Text>{labels[templateDetails.event]?.label}</Text>
                </Flex>
                {templateDetails.state ? (
                  <Flex flexDirection="row" gap={2}>
                    <Text textStyle="headingItem">Task:</Text>
                    <Text>
                      {getReminderEventsPhaseLabel(
                        {
                          event: templateDetails?.event
                        },
                        templateDetails.state
                      )}
                    </Text>
                  </Flex>
                ) : null}
                <Flex flexDirection="row" gap={2}>
                  <Text textStyle="headingItem">Scope:</Text>
                  <Text>
                    {templateDetails.scope === 'global'
                      ? 'Global/Workspace'
                      : 'Journal Specific'}
                  </Text>
                </Flex>
              </Flex>
              <Flex flexDirection="column">
                <Flex flexDirection="row" gap={2}>
                  <Text textStyle="headingItem">Configurable:</Text>
                  <Text>{templateDetails.customizable ? 'Yes' : 'No'}</Text>
                </Flex>
                <Flex flexDirection="row" gap={2}>
                  <Text textStyle="headingItem">Email Tag:</Text>
                  <Text>
                    {templateDetails.category
                      ? templateDetails.category
                      : 'N/A'}
                  </Text>
                </Flex>
                {templateDetails.event !== 'AdhocEmailSent' &&
                  templateDetails.phases && (
                    <Flex flexDirection="row" gap={2}>
                      <Text textStyle="headingItem">Workflow:</Text>
                      <Text>
                        {templateDetails.phases
                          .map(p => Phases[p as keyof typeof Phases])
                          .join(', ')}
                      </Text>
                    </Flex>
                  )}
                {templateDetails.templateId ? (
                  <Flex flexDirection="row" gap={2}>
                    <Text textStyle="headingItem">Template ID:</Text>
                    <Text>{templateDetails.templateId}</Text>
                  </Flex>
                ) : null}
              </Flex>
            </Flex>
            <Flex flexDirection="row" gap={2}>
              <Text textStyle="headingItem">To:</Text>
              <Text>{templateDetails.recipients.to}</Text>
            </Flex>
            {templateDetails.recipients.cc &&
              templateDetails.recipients.cc.length > 0 && (
                <Flex flexDirection="row" gap={2}>
                  <Text textStyle="headingItem">Cc:</Text>
                  <Text>{templateDetails.recipients.cc.join(', ')}</Text>
                </Flex>
              )}
            {templateDetails.recipients.bcc &&
              templateDetails.recipients.bcc.length > 0 && (
                <Flex flexDirection="row" gap={2}>
                  <Text textStyle="headingItem">Bcc:</Text>
                  <Text>{templateDetails.recipients.bcc.join(', ')}</Text>
                </Flex>
              )}

            <Flex flexDirection="row" gap={2}>
              <Text textStyle="headingItem">Subject:</Text>
              <Text>{templateDetails.subject}</Text>
            </Flex>
            <Flex flexDirection="column">
              <Text textStyle="headingItem">HTML Email Body</Text>
              <RichTextPreview value={templateDetails.body} />
            </Flex>
            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
            >
              <Button
                variant="secondary"
                onClick={onClose}
                borderRadius="small"
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => editModal(templateId)}
                borderRadius="small"
                leftIcon={<FontAwesomeIcon icon={faPenToSquare} />}
              >
                Edit
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex
            justifyContent="center"
            alignItems="center"
            minH={{ base: '15rem', md: '30rem' }}
          >
            <Spinner fontSize={60} />
          </Flex>
        )}
      </Flex>
    </Modal>
  );
};

export default TemplatePreview;
